/**
 * auditStrip.ts — the pure composition behind the Sales audit strip (Slice B,
 * §15.5 / §15.9), kept out of the component so vitest can pin the ordering
 * and the honest-rendering rules without a DOM.
 *
 * B-1 IS A WIRE RULE, NOT JUST A SERVER RULE. Stage transitions live in
 * sales_deal_stage_events and non-stage edits in sales_field_history; the two
 * reads stay SEPARATE on the wire and are merged HERE, client-side, for
 * display only. Nothing in this file may write a stage row back into field
 * history or vice versa — merging server-side would make one of the two
 * tables the authority on stage, which is exactly what B-1 forbids.
 *
 * DATA HONESTY (CLAUDE.md): every renderer below either shows a value the
 * read path actually carries or says plainly that it does not have one. An
 * unattributed row is an unattributed row — never "system", never a guess.
 */
import type { SalesDealStageEvent, SalesFieldHistoryEntry } from '../../../../api/types'

/** The birth row the server writes on create (history.go: historyCreated). */
export const CREATED_FIELD = 'created'

export type AuditKind = 'stage' | 'field' | 'created'

export interface AuditRow {
  key: string
  kind: AuditKind
  /** ISO instant the change was recorded. */
  at: string
  /** The acting user's id, or '' when the change is unattributed. */
  actorUserId: string
  /** The actor's display name as the read path carried it, or '' when it
   *  carried none (§15.12 AN-3). '' is NOT a name — see actorFor. */
  actorDisplayName: string
  /** Raw wire field name ('' for stage rows). */
  field: string
  /** null = the field was empty; '' = it was set to the empty string (B-5). */
  oldValue: string | null
  newValue: string | null
}

/**
 * Tie-break rank for rows sharing an instant, LOW sorts newer.
 *
 * The tie is guaranteed, not theoretical: CreateDeal writes the birth stage
 * event and the `created` history row in ONE transaction (module.go:1155 and
 * :1159), so both carry the same NOW(). In a newest-first list the creation
 * entry must sit at the BOTTOM of that instant — it is the origin every other
 * row follows from — and the stage headline above the field detail.
 */
const KIND_RANK: Record<AuditKind, number> = { stage: 0, field: 1, created: 2 }

/**
 * Compose the two reads into ONE reverse-chronological list.
 *
 * Ordering: `changedAt` DESCENDING; ties broken by KIND_RANK ascending
 * (stage, then field change, then creation); remaining ties by row id, so the
 * order is total and stable across renders and across machines.
 */
export function composeAudit(
  history: SalesFieldHistoryEntry[],
  stageEvents: SalesDealStageEvent[] = [],
): AuditRow[] {
  const rows: AuditRow[] = [
    ...stageEvents.map((e): AuditRow => ({
      key: `stage:${e.id}`,
      kind: 'stage',
      at: e.changedAt,
      actorUserId: e.actorUserId ?? '',
      actorDisplayName: e.actorDisplayName ?? '',
      field: '',
      // A stage event's "from" is empty on the birth transition; the strip
      // renders that as an entry rather than a transition.
      oldValue: e.fromStage ? e.fromStage : null,
      newValue: e.toStage,
    })),
    ...history.map((h): AuditRow => ({
      key: `field:${h.id}`,
      kind: h.field === CREATED_FIELD ? 'created' : 'field',
      at: h.changedAt,
      actorUserId: h.actorUserId ?? '',
      actorDisplayName: h.actorDisplayName ?? '',
      field: h.field,
      oldValue: h.oldValue,
      newValue: h.newValue,
    })),
  ]
  return rows.sort((a, b) => {
    if (a.at !== b.at) return a.at < b.at ? 1 : -1
    if (a.kind !== b.kind) return KIND_RANK[a.kind] - KIND_RANK[b.kind]
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  })
}

// ── Field labels ─────────────────────────────────────────────────────────────
// The wire carries the DB column name. Anything not listed falls back to a
// de-snaked version of the raw name rather than being hidden: a field we
// forgot to label is still a real change and must still be visible.

const FIELD_LABEL: Record<string, string> = {
  // Deal (§15.9 final list)
  title: 'Title',
  value_pence: 'Value',
  currency: 'Currency',
  probability: 'Probability',
  expected_close: 'Expected close',
  assigned_employee_id: 'Owner',
  account_id: 'Account',
  contact_id: 'Primary contact',
  notes: 'Notes',
  source: 'Source',
  priority: 'Priority',
  forecast_category: 'Forecast category',
  // F4b (0132): the kept-unlinked mark — empty → "kept" when set, back to
  // empty when linking an owner resets it.
  owner_link_kept: 'Owner kept unlinked',
  // 2.3c (P2-11, O-5): the override's two typed fields, audited on the deal.
  forecast_override_category: 'Forecast override',
  forecast_override_amount_pence: 'Forecast override amount',
  // Contact
  full_name: 'Name',
  email: 'Email',
  phone: 'Phone',
  status: 'Status',
  company: 'Company',
  location: 'Location',
  category: 'Category',
  prospect_segment: 'Segment',
  // Account
  name: 'Name',
  website: 'Website',
}

export function fieldLabel(field: string): string {
  const known = FIELD_LABEL[field]
  if (known) return known
  const words = field.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field
}

// ── Value rendering ──────────────────────────────────────────────────────────

/** What a rendered value IS, so the component can style empties differently. */
export type RenderedValue =
  | { kind: 'value'; text: string }
  /** The field was empty (wire null). */
  | { kind: 'empty' }
  /** The field was set to the empty string — a DIFFERENT value (B-5). */
  | { kind: 'blank' }

/** Resolvers for the two id-valued fields that have a display name. */
export interface AuditJoins {
  /** assigned_employee_id → full name, or undefined when unresolvable. */
  employeeName?: (id: string) => string | undefined
  /** account_id → account name, or undefined when unresolvable. */
  accountName?: (id: string) => string | undefined
}

/**
 * Render one stored value for display.
 *
 * B-5: values arrive as rendered TEXT. This function only ever REFORMATS what
 * the wire carries — it never invents a unit, a currency or a name:
 *
 *  - `value_pence` is an integer count of pence with NO currency on the row
 *    (currency is its own tracked field and may itself have changed), so it
 *    is grouped and labelled `pence`. Naming a currency here would be a
 *    fabrication; dropping the unit would be worse.
 *  - id fields resolve through a join and fall back to the RAW ID, never a
 *    blank — an unresolvable owner is still evidence of a change.
 *  - everything else renders verbatim.
 */
export function renderValue(field: string, value: string | null, joins: AuditJoins = {}): RenderedValue {
  if (value === null) return { kind: 'empty' }
  if (value === '') return { kind: 'blank' }

  if (field === 'value_pence' || field === 'forecast_override_amount_pence') {
    const n = Number(value)
    if (Number.isFinite(n)) return { kind: 'value', text: `${n.toLocaleString('en-GB')} pence` }
    return { kind: 'value', text: value }
  }
  if (field === 'assigned_employee_id') {
    return { kind: 'value', text: joins.employeeName?.(value) ?? value }
  }
  if (field === 'account_id') {
    return { kind: 'value', text: joins.accountName?.(value) ?? value }
  }
  return { kind: 'value', text: value }
}

// ── Stage labels ─────────────────────────────────────────────────────────────

const STAGE_LABEL: Record<string, string> = {
  new_lead: 'New Lead',
  qualified: 'Qualified',
  proposal_sent: 'Proposal Sent',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
}

/** An unmapped slug renders as the slug — visible, not swallowed. */
export function stageLabel(slug: string): string {
  return STAGE_LABEL[slug] ?? slug
}

// ── Actor ────────────────────────────────────────────────────────────────────

/**
 * How to name the person behind a row.
 *
 * There is NO read path that turns a users.id into a display name: /v1/me
 * returns the subject and role but not the id, and employees carry no user
 * link. So an attributed row shows the raw user id and an unattributed one
 * says so. "system" is deliberately NOT a fallback — the six pre-0117 stage
 * events were made by real people whose identity was never recorded, and
 * calling them "system" would state something false (B-2).
 */
export type Actor =
  | { kind: 'unattributed' }
  /** A recorded user. `displayName` is present ONLY when the read path
   *  carried a usable name (§15.12 AN-4); otherwise the label is the short id. */
  | { kind: 'user'; userId: string; displayName?: string }

/** Something the wire should never carry (AN-5: display_name only). If it ever
 *  did, the strip must not show it to every member — the id is the honest
 *  fallback, and it is what the server contract says a nameless actor gets. */
const EMAIL_SHAPED = /\S+@\S+\.\S+/

export function actorFor(row: AuditRow): Actor {
  if (!row.actorUserId) return { kind: 'unattributed' }
  // Absent means absent: '' and whitespace are NOT a name, and an
  // email-shaped value is refused rather than rendered.
  const name = row.actorDisplayName.trim()
  if (name === '' || EMAIL_SHAPED.test(name)) return { kind: 'user', userId: row.actorUserId }
  return { kind: 'user', userId: row.actorUserId, displayName: name }
}

/**
 * The AN-4 ladder, the single change point for who-did-it (§15.12):
 *   actorDisplayName → "User <first 8 of id>" → "Unknown user".
 * The full id always goes in the title, name or not.
 */
export function actorLabel(actor: Actor): string {
  if (actor.kind === 'unattributed') return 'Unknown user'
  return actor.displayName ?? `User ${actor.userId.slice(0, 8)}`
}
